import { useMemo } from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  createAppNavigation,
  type AppNavigationItem,
} from '@/app-config/navigation/create-app-navigation';
import { useAppConfig } from '@/app-config/runtime/use-app-config';
import {
  useAuth,
  useIdentity,
  useRelationships,
} from '@/features/auth/hooks/useAuth';
import {
  groupWorkspaces,
  membershipsForTenant,
} from '@/features/auth/model/memberships';
import type { NavGroup } from './nav-model';

// 教务设置 收纳「学年与学期」「开课准备」等学期初一次性配置项，置于底部并
// 默认折叠——建好后不占用日常视线，需要时再展开。collapsible 驱动 AppSidebar
// 的折叠呈现。
const NAV_GROUPS = [
  { key: '管理', labelKey: 'groups.management', collapsible: false },
  { key: '教学', labelKey: 'groups.teaching', collapsible: false },
  { key: '协作', labelKey: 'groups.collaboration', collapsible: false },
  { key: '我的', labelKey: 'groups.personal', collapsible: false },
  { key: '教务设置', labelKey: 'groups.setup', collapsible: true },
] as const satisfies ReadonlyArray<{
  key: NavGroup;
  labelKey: string;
  collapsible: boolean;
}>;

export interface WorkspaceNavigationGroup {
  readonly key: NavGroup;
  readonly label: string;
  readonly items: readonly AppNavigationItem[];
  readonly active: boolean;
  readonly collapsible: boolean;
}

// AI 工作区 是贯穿各功能的入口，与「概览」一起作为顶层置顶项，渲染机制与
// 普通导航项完全一致；概览在前，AI 工作区在后。以 routeId 识别，
// 避免改动 feature 侧路由类型。
const FEATURED_ROUTE_ID = 'thread.welcome';

/** Shared, permission-filtered model consumed by both shell renderers. */
export function useWorkspaceNavigation() {
  const { t } = useTranslation('shell');
  const appConfig = useAppConfig();
  const { tenantId = '' } = useParams({ strict: false });
  const { pathname } = useLocation();
  const { can } = useAuth();
  const identityQuery = useIdentity();
  const relationshipsQuery = useRelationships();
  const workspaces = useMemo(
    () =>
      groupWorkspaces(
        identityQuery.data?.memberships ?? [],
        relationshipsQuery.data ?? [],
      ),
    [identityQuery.data, relationshipsQuery.data],
  );
  const roles =
    workspaces.find((workspace) => workspace.tenantId === tenantId)?.roles ??
    [];
  const memberships = membershipsForTenant(
    identityQuery.data?.memberships ?? [],
    tenantId,
  );
  const items = createAppNavigation(appConfig, {
    can,
    roles,
    memberships,
    scope: { tenantId },
  });
  const isActive = (item: AppNavigationItem): boolean => {
    const target = item.to.replace('$tenantId', tenantId);
    return (
      pathname === target ||
      (!item.exact && pathname.startsWith(`${target}/`))
    );
  };
  const label = (item: AppNavigationItem): string =>
    item.labelKey ? t(item.labelKey) : item.label;
  const featured = items.find((item) => item.routeId === FEATURED_ROUTE_ID);
  const overview = items.find((item) => !item.group);
  const groups: readonly WorkspaceNavigationGroup[] = NAV_GROUPS.map(
    ({ key, labelKey, collapsible }) => {
      const groupItems = items.filter(
        (item) => item.group === key && item.routeId !== FEATURED_ROUTE_ID,
      );
      return {
        key,
        label: t(labelKey),
        items: groupItems,
        active: groupItems.some(isActive),
        collapsible,
      };
    },
  ).filter((group) => group.items.length > 0);

  return { tenantId, featured, overview, groups, label, isActive };
}
